import { Component } from "@theme/component";
import { debounce, prefersReducedMotion } from "@theme/utilities";

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ["wrapper", "content", "marqueeItems"];

  /**
   * @type {number}
   */
  #progress = 0;

  /**
   * @type {number}
   */
  #loopDistance = 0;

  /**
   * @type {number}
   */
  #duration = 0;

  /**
   * @type {number | null}
   */
  #lastTimestamp = null;

  /**
   * @type {number | null}
   */
  #frame = null;

  /**
   * @type {number}
   */
  #lastMeasuredContentWidth = 0;

  /**
   * @type {ResizeObserver}
   */
  #resizeObserver = new ResizeObserver(() => {
    const contentWidth = this.refs.content?.offsetWidth ?? 0;
    if (Math.abs(contentWidth - this.#lastMeasuredContentWidth) < 1) return;

    this.#lastMeasuredContentWidth = contentWidth;
    this.#handleResize();
  });

  async connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    this.querySelectorAll("img").forEach((image) => {
      image.loading = "eager";
    });
    await this.#waitForImages();
    const numberOfCopies = await this.#queryNumberOfCopies();
    const speed = this.#calculateSpeed(numberOfCopies);

    this.#addRepeatedItems(numberOfCopies);
    this.#duplicateContent();
    this.#setSpeed(speed);
    this.#updateAnimationMetrics(speed, false);
    this.#lastMeasuredContentWidth = this.refs.content.offsetWidth;
    this.#resizeObserver.observe(this.refs.content);

    if (!prefersReducedMotion()) {
      this.#startAnimation();
      window.visualViewport?.addEventListener(
        "resize",
        this.#handleViewportChange,
        { passive: true },
      );
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopAnimation();
    this.#resizeObserver.disconnect();
    window.visualViewport?.removeEventListener(
      "resize",
      this.#handleViewportChange,
    );
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  /**
   * @param {number} value
   */
  #setSpeed(value) {
    this.style.setProperty("--marquee-speed", `${value}s`);
  }

  #waitForImages() {
    const images = this.querySelectorAll("img");

    return Promise.all(
      Array.from(images, (image) => {
        if (image.complete) return Promise.resolve();

        return new Promise((resolve) => {
          image.addEventListener("load", resolve, { once: true });
          image.addEventListener("error", resolve, { once: true });
        });
      }),
    );
  }

  async #queryNumberOfCopies() {
    const { marqueeItems } = this.refs;

    return new Promise((resolve) => {
      if (!marqueeItems[0]) {
        return setTimeout(() => resolve(1), 0);
      }

      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          const firstEntry = entries[0];
          if (!firstEntry) return;

          intersectionObserver.disconnect();

          const { width: marqueeWidth } = firstEntry.rootBounds ?? {
            width: 0,
          };
          const { width: marqueeItemsWidth } = firstEntry.boundingClientRect;

          setTimeout(() => {
            resolve(
              marqueeItemsWidth === 0
                ? 1
                : Math.ceil(marqueeWidth / marqueeItemsWidth),
            );
          }, 0);
        },
        { root: this },
      );

      intersectionObserver.observe(marqueeItems[0]);
    });
  }

  /**
   * @param {number} numberOfCopies
   */
  #calculateSpeed(numberOfCopies) {
    const speedFactor = Number(this.getAttribute("data-speed-factor"));
    return Math.sqrt(numberOfCopies) * speedFactor;
  }

  #handleResize = debounce(async () => {
    const newNumberOfCopies = await this.#queryNumberOfCopies();
    const currentNumberOfCopies = this.refs.content.children.length;
    const speed = this.#calculateSpeed(newNumberOfCopies);

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed(speed);
    this.#updateAnimationMetrics(speed);
    this.#syncAnimation();
    this.#lastMeasuredContentWidth = this.refs.content.offsetWidth;
  }, 250);

  #handleViewportChange = () => {
    this.#syncAnimation();
  };

  #startAnimation() {
    if (this.#frame !== null || prefersReducedMotion()) return;

    this.#lastTimestamp = performance.now();
    this.#frame = requestAnimationFrame(this.#tick);
  }

  #stopAnimation() {
    if (this.#frame !== null) {
      cancelAnimationFrame(this.#frame);
    }

    this.#frame = null;
    this.#lastTimestamp = null;
  }

  /**
   * @param {number} timestamp
   */
  #tick = (timestamp) => {
    this.#advanceAnimation(timestamp);
    this.#frame = requestAnimationFrame(this.#tick);
  };

  #syncAnimation(timestamp = performance.now()) {
    this.#advanceAnimation(timestamp);
  }

  /**
   * @param {number} timestamp
   */
  #advanceAnimation(timestamp) {
    if (
      prefersReducedMotion() ||
      this.#loopDistance <= 0 ||
      this.#duration <= 0
    ) {
      return;
    }

    if (this.#lastTimestamp === null) {
      this.#lastTimestamp = timestamp;
      this.#applyTransform();
      return;
    }

    const delta = Math.max(0, timestamp - this.#lastTimestamp);
    this.#lastTimestamp = timestamp;

    const distancePerMillisecond = this.#loopDistance / this.#duration;
    this.#progress =
      (this.#progress + delta * distancePerMillisecond) % this.#loopDistance;

    this.#applyTransform();
  }

  /**
   * @param {number} speed
   * @param {boolean} [preserveProgress=true]
   */
  #updateAnimationMetrics(speed, preserveProgress = true) {
    const previousLoopDistance = this.#loopDistance;
    const progressRatio =
      preserveProgress && previousLoopDistance > 0
        ? this.#progress / previousLoopDistance
        : 0;

    this.#duration = speed * 1000;
    this.#loopDistance = Math.max(
      this.refs.content.offsetWidth + this.#getGapWidth(),
      1,
    );
    this.#progress = progressRatio * this.#loopDistance;
    this.#lastTimestamp = performance.now();
    this.#applyTransform();
  }

  #getGapWidth() {
    const wrapperStyles = getComputedStyle(this.refs.wrapper);
    return parseFloat(wrapperStyles.columnGap || wrapperStyles.gap || "0") || 0;
  }

  #applyTransform() {
    const translateX =
      this.getAttribute("data-movement-direction") === "reverse"
        ? this.#progress - this.#loopDistance
        : -this.#progress;

    this.refs.wrapper.style.transform = `translate3d(${translateX}px, 0, 0)`;
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (
      this.refs.content.cloneNode(true)
    );

    clone.setAttribute("aria-hidden", "true");
    clone.removeAttribute("ref");
    clone.querySelectorAll("[ref]").forEach((element) => {
      element.removeAttribute("ref");
    });

    this.refs.wrapper.appendChild(clone);
  }

  /**
   * @param {number} numberOfCopies
   */
  #addRepeatedItems(numberOfCopies) {
    const { content, marqueeItems } = this.refs;

    if (!marqueeItems[0]) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = /** @type {HTMLElement} */ (
        marqueeItems[0].cloneNode(true)
      );
      clone.removeAttribute("ref");
      clone.querySelectorAll("[ref]").forEach((element) => {
        element.removeAttribute("ref");
      });
      content.appendChild(clone);
    }
  }

  /**
   * @param {number} numberOfCopies
   */
  #removeRepeatedItems(numberOfCopies) {
    const { content } = this.refs;
    const children = Array.from(content.children);

    const itemsToRemove = Math.min(numberOfCopies, children.length - 1);

    for (let i = 0; i < itemsToRemove; i++) {
      content.lastElementChild?.remove();
    }
  }
}

if (!customElements.get("marquee-component")) {
  customElements.define("marquee-component", MarqueeComponent);
}
